import React from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { GeneralFeedback } from '../utils/dynamodb';

interface FeedbackGeneralTableProps {
  feedback: GeneralFeedback[];
  isLoading: boolean;
  onExportCSV: () => void;
  RefreshButton: React.ReactNode;
}

export const FeedbackGeneralTable: React.FC<FeedbackGeneralTableProps> = ({
  feedback,
  isLoading,
  onExportCSV,
  RefreshButton,
}) => {
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              General Feedback
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              User feedback submitted through the application
            </p>
          </div>
          <div className="flex items-center gap-3">
            {RefreshButton}
            <button
              onClick={onExportCSV}
              disabled={isLoading || feedback.length === 0}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <div>
            <span className="font-medium text-gray-900">{feedback.length}</span>{' '}
            total feedback items
          </div>
          <div>
            <span className="font-medium text-gray-900">
              {new Set(feedback.map((f) => f.user_id)).size}
            </span>{' '}
            unique users
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-600">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Loading feedback...</span>
            </div>
          </div>
        ) : feedback.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium mb-2">No feedback found</p>
              <p className="text-sm">
                No general feedback has been submitted yet.
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    Comment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {feedback.map((item) => (
                  <tr
                    key={`${item.PK}-${item.SK}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.user_id}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md">
                        <div className="break-words">{item.comment}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.formatted_created_at || item.created_at}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
